/**
 * Language Selection Page — /language
 *
 * A self-contained screen for picking the app language.
 * Uses the I18nProvider context to change locale reactively.
 *
 * Features:
 * - Shows all 11 supported locales with flag, native label & English label
 * - Highlights the currently active locale
 * - Persists to DB automatically via setLocale()
 * - Handles RTL locales (Arabic, Hebrew) with a direction indicator
 * - Accessible: keyboard navigation, ARIA roles
 * - Premium animated card grid matching the app design system
 */

import { createMemo, createSignal, For, Show } from "solid-js";
import { createRoute } from "@tanstack/solid-router";
import { appRoute } from "./route";
import { filterLocales } from "@vigia/shared";
import type { SupportedLocale } from "@vigia/config/locales";
import { useI18n } from "@/providers/i18n-provider";
import { PageContainer, TextField } from "@fleet/ui";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Globe, Loader2, RotateCcw, Search, X } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { LocaleCard } from "./profile/locale-card";

// ─── Page Component ────────────────────────────────────────────────────────────

function LanguagePage() {
  const { locale, setLocale, isLoading, t } = useI18n();
  const [search, setSearch] = createSignal("");

  const filteredLocales = createMemo(() => filterLocales(search()));

  const handleSelect = async (code: SupportedLocale) => {
    if (code === locale() || isLoading()) {
      return;
    }
    await setLocale(code);
  };

  return (
    <div class="flex flex-1 flex-col overflow-hidden bg-background">
      {/* ── Header ────────────────────────────────────────────── */}
      <PageSubHeader
        title={t(msg`Language`)}
        backTo="/profile"
        icon={Globe}
        rightElement={
          <Show when={isLoading()}>
            <Loader2 class="h-4 w-4 animate-spin text-muted-foreground" />
          </Show>
        }
      />

      {/* ── Body ──────────────────────────────────────────────── */}
      <PageContainer maxWidth="2xl" class="space-y-8">
        {/* Description */}
        <div class="space-y-1">
          <h2 class="text-2xl font-bold tracking-tight">{t(msg`Select Language`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(msg`Your preference is synced across all your devices automatically.`)}
          </p>
        </div>

        {/* Search Bar */}
        <div class="relative">
          <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <TextField
            type="text"
            placeholder={t(msg`Search languages...`)}
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
            class="pl-10 pr-10"
          />
          <Show when={search()}>
            <button
              type="button"
              onClick={() => setSearch("")}
              class="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-muted"
              aria-label={t(msg`Clear search`)}
            >
              <X class="h-3 w-3 text-muted-foreground" />
            </button>
          </Show>
        </div>

        {/* Locale Grid */}
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Show
            when={filteredLocales().length > 0}
            fallback={
              <div class="col-span-full py-20 text-center">
                <Search class="mx-auto h-12 w-12 text-muted/50" />
                <p class="mt-4 text-lg font-medium text-muted-foreground">
                  {t(msg`No languages found for "{query}"`, { query: search() })}
                </p>
              </div>
            }
          >
            <For each={filteredLocales()}>
              {(def) => (
                <LocaleCard
                  code={def.code}
                  flag={def.flag}
                  label={def.label}
                  englishLabel={def.englishLabel}
                  isActive={locale() === def.code}
                  isLoading={isLoading()}
                  onSelect={handleSelect}
                />
              )}
            </For>
          </Show>
        </div>

        {/* Reset hint */}
        <div class="flex items-start gap-3 rounded-xl border border-border/50 bg-muted/30 p-4 text-sm text-muted-foreground">
          <RotateCcw class="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {t(
              msg`Language changes take effect immediately on the web. Mobile apps may require a restart to correctly apply RTL/LTR layout changes.`,
            )}
          </p>
        </div>
      </PageContainer>
    </div>
  );
}

// ─── Route ─────────────────────────────────────────────────────────────────────

export const languageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/language",
  component: LanguagePage,
});
